import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ExternalEventService } from '@base/services/external-event.service';
import { LoadMFESrvAbstract } from '@base/abstracts/services/load-mfe.abstract';
import { DynamicImportLoaderService } from '@base/services/private/dynamic-import.service';
import { GlobalStateService } from '@base/services/private/global-state.service';
import { ModalService } from '@base/services/private/modal.service';
import { SessionService } from '@base/services/private/session.service';
import { TranslateService } from '@base/services/private/translate.service';
import { FaroService } from '@core/services/faro.service';
import { WhiteLabel } from '@config/whitelabel-keys.constant';
import { getMfeAttributes } from '@base/utils';
import { environment } from 'environments/environment';
import { InformaMfeEvent, InformaMfeNavigateValue } from '@base/models/others/informa-mfe-event';
import { RequestLabelService } from '@base/services/private/request-label.service';
import { MeetService } from '@base/services/private/meet.service';
import { Attendee } from '@base/models/responses/attendee.res';
import {
  handleInformaMfeUserAction,
  buildUserContext,
  mapHostLanguageToInforma,
  resolveTranslateEntity,
  isInformaGroupMember,
} from '@base/utils/informa-mfe.utils';
import { GlobalState } from '@base/models/others/global-state';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { TypeOfRequestLabel } from '@base/enums/request-label.enum';
import { NavigationEnd, Router } from '@angular/router';
import { filter, lastValueFrom } from 'rxjs';
import { InformaFloorplanScriptService } from '@base/services/informa-floorplan-script.service';

@Component({
  selector: 'app-modal-external-floorplan',
  templateUrl: './modal-external-floorplan.component.html',
  styleUrls: ['./modal-external-floorplan.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ModalHeaderComponent, TranslatePipe],
})
export class ModalExternalFloorplanComponent implements AfterViewInit, OnDestroy {
  readonly modalId = input('');
  readonly isModal = input(true);
  readonly floorplanId = input<number | null>(null);
  readonly markerId = input<number | null>(null);
  readonly containerRef = viewChild<ElementRef>('containerRef');

  readonly isLoading = signal(true);
  readonly loadError = signal(false);

  private readonly externalEventSrv = inject(ExternalEventService);
  private readonly requestLabelSrv = inject(RequestLabelService);
  private readonly meetSrv = inject(MeetService);
  private readonly localBookmarks = new Set<string>();
  private readonly dynamicImportLoaderSrv = inject(DynamicImportLoaderService);
  private readonly globalStateSrv = inject(GlobalStateService);
  private readonly modalSrv = inject(ModalService);
  private readonly faroSrv = inject(FaroService);
  private readonly sessionSrv = inject(SessionService);
  private readonly translateSrv = inject(TranslateService);
  private readonly scriptSrv = inject(InformaFloorplanScriptService);

  private readonly router = inject(Router);

  private readonly destroyRef = inject(DestroyRef);
  private loadMfeSrv: LoadMFESrvAbstract | null = null;
  private mfeElement: HTMLElement | null = null;
  private mfeEventHandler: EventListener | null = null;
  private informaAuthToken = '';
  private globalState: GlobalState | null = this.globalStateSrv.getState();
  private mountTimer: ReturnType<typeof setTimeout> | null = null;
  private isDestroyed = false;
  private hasScriptLease = false;

  /** Closes this modal via its registered modal ID. */
  close(): void {
    this.modalSrv.close('modal-external-floorplan');
  }

  /**
   * Bootstraps the MFE on view init.
   * Angular does not await lifecycle hooks — IIFE + catch guards against unhandled rejections.
   */
  ngAfterViewInit(): void {
    (async () => {
      await this.initMfeSrv();
      this.syncMfeLanguage();
      this.scheduleMfeMount();
    })().catch((error) => {
      console.error('ModalExternalFloorplan: init failed:', error);
      this.isLoading.set(false);
      this.loadError.set(true);
    });
    if (this.isModal()) {
      this.router.events
        .pipe(
          filter((e) => e instanceof NavigationEnd),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => this.close());
    }
  }

  /**
   * Resolves the latest global state and dynamically loads the LoadMfe service.
   */
  private async initMfeSrv(): Promise<void> {
    this.globalState = this.globalStateSrv.getState();
    this.loadMfeSrv = await this.dynamicImportLoaderSrv.loadService<LoadMFESrvAbstract>(WhiteLabel.SERVICES.LoadMfe);
  }

  /**
   * Schedules the MFE mount after a short delay so the ViewChild containerRef
   * is guaranteed to be in the DOM regardless of network speed.
   */
  private scheduleMfeMount(): void {
    this.mountTimer = setTimeout(() => {
      this.mountTimer = null;
      if (this.isDestroyed) return;
      this.loadAndMount().catch((error) => {
        console.error('ModalExternalFloorplan: mount failed:', error);
      });
    }, 500);
  }

  /**
   * Loads the Informa Floorplan script and mounts the MFE.
   * The MFE auth token is fetched later, only after an eligible Informa member requests it.
   */
  private async loadAndMount(): Promise<void> {
    if (this.isDestroyed) return;
    this.isLoading.set(true);
    this.loadError.set(false);
    const pgId = this.globalState?.loader?.pg_id ?? '';
    if (!this.loadMfeSrv?.canUseExternalFloorplan(pgId, this.globalStateSrv.isAnon())) {
      this.isLoading.set(false);
      return;
    }
    if (this.isDestroyed) return;
    try {
      if (!this.hasScriptLease) {
        await this.scriptSrv.acquire(this.loadMfeSrv);
        if (this.isDestroyed) {
          this.scriptSrv.release();
          return;
        }
        this.hasScriptLease = true;
      }
    } catch (error) {
      console.error('ModalExternalFloorplan: script load failed:', error);
      this.loadError.set(true);
      this.isLoading.set(false);
      return;
    }
    if (this.isDestroyed) return;
    this.instanceFloorplanMfe();
    this.isLoading.set(false);
  }

  /** Retries the full MFE load-and-mount sequence. Bound to the error state retry button. */
  retryLoad(): void {
    (async () => {
      await this.initMfeSrv();
      await this.loadAndMount();
    })().catch((error) => {
      console.error('ModalExternalFloorplan: retry failed:', error);
      this.isLoading.set(false);
      this.loadError.set(true);
    });
  }

  /**
   * Fetches the Informa auth token and caches it.
   * Pushes the error to Faro before re-throwing so callers can bail out cleanly.
   */
  private async loadInformaAuthToken(): Promise<void> {
    try {
      this.informaAuthToken = await this.externalEventSrv.getInformaToken();
    } catch (error) {
      this.faroSrv.pushError(error as Error);
      throw error;
    }
  }

  /**
   * Creates the `informa-floorplan-module` custom element, sets required attributes
   * (`event-hash`, `auth-token`, `platform`, `lang`, `event-color`, `api-base-url`),
   * attaches the `informaFloorplanListener` event listener, and appends it to the container.
   * Optional `floorplan-id` and `marker-id` are only set when provided.
   */
  private instanceFloorplanMfe(): void {
    const loader = this.globalState?.loader;
    const validate = this.globalState?.validate;
    const isAnon = this.globalStateSrv.isAnon();
    if (!loader || (!validate && !isAnon)) {
      this.loadError.set(true);
      this.isLoading.set(false);
      return;
    }
    this.mfeElement = document.createElement('informa-floorplan-module');
    Object.assign(this.mfeElement, { faroInstance: this.faroSrv.getInstance() });
    const { event_color } = loader;
    this.mfeElement.setAttribute('event-hash', environment.informaEventCode);
    if (!isAnon) {
      this.mfeElement.setAttribute('auth-token', this.informaAuthToken);
    }
    this.mfeElement.setAttribute('platform', 'WEB');
    this.mfeElement.setAttribute('lang', this.getMappedLanguage(validate?.person_lang));
    this.mfeElement.setAttribute('event-color', event_color);
    this.mfeElement.setAttribute('api-base-url', environment.informaApiBaseUrl);
    if (this.floorplanId() != null) {
      this.mfeElement.setAttribute('floorplan-id', String(this.floorplanId()));
    }
    if (this.markerId() != null) {
      this.mfeElement.setAttribute('marker-id', String(this.markerId()));
    }
    this.mfeElement.setAttribute('user-context', buildUserContext(this.localBookmarks));
    this.mfeEventHandler = this.onExternalFloorplanEvent.bind(this) as EventListener;
    this.mfeElement.addEventListener('informaFloorplanListener', this.mfeEventHandler);
    this.containerRef()?.nativeElement?.appendChild(this.mfeElement);
    const attrs = getMfeAttributes(this.mfeElement, ['auth-token']);
    this.faroSrv.pushMfeInit('informa-floorplan-module', attrs);
  }

  private syncMfeLanguage(): void {
    this.translateSrv
      .getCurrentLanguage()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((language) => {
        this.mfeElement?.setAttribute('lang', mapHostLanguageToInforma(language));
      });
  }

  private getMappedLanguage(authenticatedLanguage?: string): string {
    const hostLanguage = this.globalStateSrv.isAnon()
      ? (this.sessionSrv.getItem('lang') as string | null)
      : authenticatedLanguage;

    return mapHostLanguageToInforma(hostLanguage);
  }

  /**
   * Handles `informaFloorplanListener` CustomEvents from the Informa Floorplan MFE.
   * `AUTH_REQUIRED`: refreshes `auth-token` for logged-in Informa members; guests and other users are ignored.
   * `NAVIGATE`: translates entity ids and opens the appropriate modal; replies via
   *   `navigate-result`. Unsupported targets get a FAILED reply — never a silent drop.
   * `USER_ACTION`: forwarded to analytics log.
   * `ERROR`: logged to console.
   * `READY`, `LOADING`, `CLOSED`: acknowledged silently.
   */
  private onExternalFloorplanEvent(event: CustomEvent<InformaMfeEvent>): void {
    const { type, value } = event.detail;
    console.info('Informa floorplan MFE event:', { type });
    switch (type) {
      // start of custom cosmoprof-asia-2026: https://jublia.atlassian.net/browse/JO-XXXX
      case 'NAVIGATE':
        void this.handleFloorplanNavigate(value as InformaMfeNavigateValue);
        break;
      // end of custom cosmoprof-asia-2026: https://jublia.atlassian.net/browse/JO-XXXX
      case 'AUTH_REQUIRED':
        void this.handleFloorplanAuthRequired();
        break;
      case 'USER_ACTION':
        this.handleUserAction(value as Record<string, unknown>);
        break;
      case 'ERROR':
        console.error('InformaFloorplanMFE error:', { type });
        break;
      case 'READY':
      case 'LOADING':
      case 'CLOSED':
        break;
      default:
        console.warn(`Unhandled Informa Floorplan MFE event type: ${type}`);
    }
  }

  private async handleFloorplanAuthRequired(): Promise<void> {
    // Guests and non-members mount without an Informa token and must not start an Informa auth flow.
    const pgId = this.globalState?.loader?.pg_id ?? '';
    const isInformaGroup = isInformaGroupMember(this.loadMfeSrv?.externalMfeGroupIds, pgId);

    if (this.globalStateSrv.isAnon() || !isInformaGroup) return;
    this.externalEventSrv.clearToken();
    try {
      await this.loadInformaAuthToken();
      this.mfeElement?.setAttribute('auth-token', this.informaAuthToken);
    } catch (err) {
      console.error('Informa floorplan token refresh failed:', err);
    }
  }

  /**
   * Resolves the entity id, translates it, then opens the matching modal.
   * Floorplan MFE sends AGENDA_SESSION id in context.sessionId instead of entityId.
   * Replies via `navigate-result` in all cases — never a silent drop.
   */
  private async handleFloorplanNavigate(nav: InformaMfeNavigateValue): Promise<void> {
    const entityId = nav?.entityId || (nav?.target === 'AGENDA_SESSION' ? nav?.context?.sessionId : undefined);
    if (!entityId) {
      this.replyNavigate(nav?.requestId ?? '', 'FAILED', 'MISSING_ENTITY_ID');
      return;
    }
    if (nav.target === 'AGENDA_SESSION') {
      let agendaHash: string;
      try {
        agendaHash = await this.externalEventSrv.translateExternalId('AGENDA', entityId);
      } catch (err) {
        console.error('Informa floorplan: translateExternalId for AGENDA_SESSION failed:', err);
        this.replyNavigate(nav.requestId, 'FAILED', 'EXTERNAL_ID_TRANSLATE_ERROR');
        return;
      }
      this.modalSrv.showModalAgendaDetail({ addsData: { sessionId: agendaHash } });
      this.replyNavigate(nav.requestId, 'OPENED');
      return;
    }
    const entity = resolveTranslateEntity(nav);
    if (!entity) {
      console.warn(`Informa floorplan: unhandled NAVIGATE target: ${nav.target}`);
      this.replyNavigate(nav.requestId, 'FAILED', 'UNSUPPORTED_TARGET');
      return;
    }
    let entityHash: string;
    try {
      entityHash = await this.externalEventSrv.translateExternalId(entity, entityId);
    } catch (err) {
      console.error('Informa floorplan translate external id failed:', err);
      this.replyNavigate(nav.requestId, 'FAILED', 'EXTERNAL_ID_TRANSLATE_ERROR');
      return;
    }
    switch (nav.target) {
      case 'EXHIBITOR':
        this.modalSrv.showModalExhibitorDetail({ addsData: { exhibitorId: entityHash } });
        this.replyNavigate(nav.requestId, 'OPENED');
        break;
      case 'PEOPLE':
        this.modalSrv.showModalPeopleDetail({ addsData: { peopleId: entityHash } });
        this.replyNavigate(nav.requestId, 'OPENED');
        break;
      case 'PRODUCT':
        this.modalSrv.showModalProductDetail({ addsData: { productId: entityHash } });
        this.replyNavigate(nav.requestId, 'OPENED');
        break;
      case 'MEETING_REQUEST':
        if (nav.entityType === 'EXHIBITOR') {
          await this.handleExhibitorMeetingRequest(entityHash, nav.requestId);
        } else {
          this.openRequestMeetModal(entityHash, nav.requestId);
        }
        break;
      default:
        console.warn(`Informa floorplan: unhandled entity target: ${nav.target}`);
        this.replyNavigate(nav.requestId, 'FAILED', 'UNSUPPORTED_TARGET');
    }
  }

  /**
   * Fetches the exhibitor's attendee list, then routes to:
   * - exactly 1 attendee: BM modal directly
   * - more than 1: people-list modal so the user picks who to meet
   * - 0 attendees: exhibitor detail (no one available to meet)
   */
  private async handleExhibitorMeetingRequest(companyHash: string, requestId: string): Promise<void> {
    let attendees: Attendee[];
    try {
      const res = await lastValueFrom(this.meetSrv.getCompanyDetail(companyHash));
      attendees = res.attendees ?? [];
    } catch (err) {
      console.error('Informa floorplan: getCompanyDetail failed:', err);
      this.replyNavigate(requestId, 'FAILED', 'COMPANY_DETAIL_ERROR');
      return;
    }
    if (attendees.length === 1) {
      this.openRequestMeetModal(attendees[0].id_attendee, requestId);
      return;
    }
    if (attendees.length > 1) {
      this.modalSrv.showModalPeopleList({ addsData: { attendees } });
      this.replyNavigate(requestId, 'OPENED');
      return;
    }
    this.modalSrv.showModalExhibitorDetail({ addsData: { exhibitorId: companyHash } });
    this.replyNavigate(requestId, 'OPENED');
  }

  /** Opens the meeting-request modal and wires `navigate-result` replies (COMPLETED / DISMISSED) to the MFE. */
  private openRequestMeetModal(peopleId: string, requestId: string): void {
    const meetRef = this.modalSrv.showModalRequestMeet({
      addsData: { peopleId, action: TypeOfRequestLabel.REQUEST_MEETING, isSkipCheckMeeting: false, isCloseAll: false },
      addsConfig: { modalId: 'modal-meet' },
    });
    const bm = meetRef?.componentInstance as { meetingRequestCompleted?: { subscribe(fn: () => void): void } };
    let meetCompleted = false;
    bm?.meetingRequestCompleted?.subscribe(() => {
      meetCompleted = true;
      this.replyNavigate(requestId, 'COMPLETED');
    });
    meetRef?.closed.subscribe(() => {
      if (!meetCompleted) this.replyNavigate(requestId, 'DISMISSED');
    });
    meetRef?.dismissed.subscribe(() => this.replyNavigate(requestId, 'DISMISSED'));
  }

  /** Forwards a `USER_ACTION` payload to the shared Informa MFE util (e.g. bookmark toggle). */
  private async handleUserAction(payload: Record<string, unknown>): Promise<void> {
    try {
      await handleInformaMfeUserAction(payload, {
        localBookmarks: this.localBookmarks,
        externalEventSrv: this.externalEventSrv,
        requestLabelSrv: this.requestLabelSrv,
        logPrefix: 'Informa floorplan',
      });
      this.mfeElement?.setAttribute('user-context', buildUserContext(this.localBookmarks));
    } catch (error) {
      console.error('Informa floorplan: bookmark update failed:', error);
    }
  }

  /**
   * Sets the `navigate-result` attribute on the MFE element to acknowledge a NAVIGATE event.
   * Errors during serialisation are caught and logged — never a crash.
   *
   * @param requestId - Echo of the `requestId` from the incoming NAVIGATE event.
   * @param outcome - Terminal outcome: OPENED, COMPLETED, DISMISSED, or FAILED.
   * @param errorCode - Stable error code, present only on FAILED outcomes.
   */
  private replyNavigate(
    requestId: string,
    outcome: 'OPENED' | 'COMPLETED' | 'DISMISSED' | 'FAILED',
    errorCode?: string
  ): void {
    if (!this.mfeElement || !requestId) return;
    try {
      const result: Record<string, string> = { requestId, outcome };
      if (errorCode) result['errorCode'] = errorCode;
      this.mfeElement.setAttribute('navigate-result', JSON.stringify(result));
    } catch {
      console.error('Informa floorplan: failed to set navigate-result attribute');
    }
  }

  /**
   * Removes the `informaFloorplanListener` event listener, nulls the element reference,
   * and unloads the Informa Floorplan script to prevent memory leaks.
   */
  ngOnDestroy(): void {
    this.isDestroyed = true;
    if (this.mountTimer !== null) {
      clearTimeout(this.mountTimer);
      this.mountTimer = null;
    }
    if (this.mfeElement && this.mfeEventHandler) {
      this.mfeElement.removeEventListener('informaFloorplanListener', this.mfeEventHandler);
      this.mfeEventHandler = null;
    }
    this.mfeElement = null;
    if (this.hasScriptLease) {
      this.scriptSrv.release();
      this.hasScriptLease = false;
    }
  }
}
